// De sleepgreep naast een blok, en het menu dat eronder hangt.
//
// Twee dingen die je in Notion de hele dag gebruikt en die je hier miste: een alinea ergens
// anders neerzetten zonder knippen en plakken, en "dit moet eigenlijk een kopje zijn" zonder
// eerst de hele regel te selecteren.
//
// Hij verschijnt alleen op een apparaat met een echte muisaanwijzer. Op een telefoon is er geen
// "erboven zweven", zou de greep dus altijd staan, en is er in de kantlijn ook geen ruimte voor;
// daar doe je dit met de werkbalk.

import { useEffect, useRef, useState } from "react";
import type { Editor as TipTapEditor } from "@tiptap/react";
import { NodeSelection } from "@tiptap/pm/state";
import { Icoon } from "./Icoon.tsx";
import { naarMarkdown } from "../../../server/kern/inhoud.ts";

export type ZwevendBlok = { pos: number; top: number; links: number; hoogte: number };

/**
 * Welk blok zweeft de muis boven?
 *
 * Niet op de x van de muis zoeken maar midden in de tekstkolom: als je naar de greep beweegt
 * sta je ernáást, en dan zou ProseMirror niets vinden en de greep onder je hand wegspringen.
 */
export function useZwevendBlok(editor: TipTapEditor | null, blad: HTMLElement | null): ZwevendBlok | null {
  const [blok, setBlok] = useState<ZwevendBlok | null>(null);

  useEffect(() => {
    if (!editor || !blad) return;
    if (!matchMedia("(hover: hover)").matches) return;

    function opBeweging(gebeurtenis: MouseEvent) {
      const kijk = editor!.view;
      const tekst = kijk.dom as HTMLElement;
      const vlak = tekst.getBoundingClientRect();
      const punt = kijk.posAtCoords({ left: vlak.left + vlak.width / 2, top: gebeurtenis.clientY });
      if (!punt) return setBlok(null);

      // `pos` en niet `inside`: bij tekst wijst `inside` naar de alinea zelf, en die zit in een
      // lijst soms twee lagen diep. Vanaf de tekstpositie omhoog lopen naar het bovenste niveau
      // geeft altijd het blok dat je als geheel wilt oppakken. Een blok zonder tekst (een
      // afbeelding, een streep) heeft geen diepte; daar is `inside` wél de goede plek.
      const plek = kijk.state.doc.resolve(punt.pos);
      const pos = plek.depth >= 1 ? plek.before(1) : punt.inside;
      if (pos < 0) return setBlok(null);
      const dom = kijk.nodeDOM(pos) as HTMLElement | null;
      if (!dom?.getBoundingClientRect) return setBlok(null);

      const vak = dom.getBoundingClientRect();
      const bladVak = blad!.getBoundingClientRect();
      // Verstopt onder een ingeklapte kop: dan hoort er ook geen greep te zweven.
      if (vak.height === 0) return setBlok(null);
      setBlok({
        pos,
        top: vak.top - bladVak.top + blad!.scrollTop,
        // Vlak naast de tekst, niet aan de rand van het venster: de tekstkolom staat in het
        // midden, dus aan de rand zou de greep een decimeter van zijn blok af staan.
        links: Math.max(0, vak.left - bladVak.left - 40),
        hoogte: vak.height
      });
    }

    function opVertrek() {
      setBlok(null);
    }

    blad.addEventListener("mousemove", opBeweging);
    blad.addEventListener("mouseleave", opVertrek);
    return () => {
      blad.removeEventListener("mousemove", opBeweging);
      blad.removeEventListener("mouseleave", opVertrek);
    };
  }, [editor, blad]);

  return blok;
}

// Zonder icoontjes: vier keer hetzelfde plaatje naast Tekst, Kop 1, Kop 2 en Kop 3 zegt niets,
// en dan is de naam alleen rustiger. Het merkje links is de vorm zelf — een streep voor tekst,
// een dikke voor een kop.
type Omzetting = { naam: string; merk: string; doe: (editor: TipTapEditor) => void };

const OMZETTINGEN: Omzetting[] = [
  { naam: "Tekst", merk: "¶", doe: (e) => e.chain().focus().setParagraph().run() },
  { naam: "Kop 1", merk: "H1", doe: (e) => e.chain().focus().setHeading({ level: 1 }).run() },
  { naam: "Kop 2", merk: "H2", doe: (e) => e.chain().focus().setHeading({ level: 2 }).run() },
  { naam: "Kop 3", merk: "H3", doe: (e) => e.chain().focus().setHeading({ level: 3 }).run() },
  { naam: "Opsomming", merk: "•", doe: (e) => e.chain().focus().toggleBulletList().run() },
  { naam: "Checklist", merk: "☑", doe: (e) => e.chain().focus().toggleTaskList().run() },
  { naam: "Citaat", merk: "❝", doe: (e) => e.chain().focus().toggleBlockquote().run() },
  { naam: "Codeblok", merk: "</>", doe: (e) => e.chain().focus().toggleCodeBlock().run() },
  { naam: "Uitgelicht blok", merk: "!", doe: (e) => e.chain().focus().wrapIn("callout", { soort: "waarschuwing" }).run() }
];

export function Blokgreep({
  editor,
  blok,
  opMelding
}: {
  editor: TipTapEditor;
  blok: ZwevendBlok;
  opMelding?: (tekst: string) => void;
}) {
  const [menu, setMenu] = useState(false);
  const vakRef = useRef<HTMLDivElement | null>(null);

  // Het menu hoort bij één blok; ga je met de muis ergens anders heen, dan slaat het nergens
  // meer op. Vandaar dat het sluit zodra het blok eronder verandert.
  useEffect(() => setMenu(false), [blok.pos]);

  useEffect(() => {
    if (!menu) return;
    function opKlikBuiten(gebeurtenis: MouseEvent) {
      if (!vakRef.current?.contains(gebeurtenis.target as globalThis.Node)) setMenu(false);
    }
    function opToets(gebeurtenis: KeyboardEvent) {
      if (gebeurtenis.key === "Escape") setMenu(false);
    }
    addEventListener("mousedown", opKlikBuiten);
    addEventListener("keydown", opToets);
    return () => {
      removeEventListener("mousedown", opKlikBuiten);
      removeEventListener("keydown", opToets);
    };
  }, [menu]);

  const knoop = () => editor.state.doc.nodeAt(blok.pos);

  /** De cursor ín het blok zetten; de meeste commando's werken op waar je staat. */
  function kiesBlok(): boolean {
    const eigen = knoop();
    if (!eigen) return false;
    editor.chain().setTextSelection(blok.pos + 1).run();
    return true;
  }

  /**
   * Slepen doet ProseMirror zelf, zolang hij weet wát er gesleept wordt: we zetten de selectie
   * op het hele blok en geven hem datzelfde stukje document mee als vracht. Zonder die laatste
   * regel sleep je de tekst als platte letters en raak je de opmaak kwijt.
   */
  function opSleepStart(gebeurtenis: React.DragEvent) {
    const kijk = editor.view;
    const eigen = knoop();
    if (!eigen) return;
    const keuze = NodeSelection.create(kijk.state.doc, blok.pos);
    kijk.dispatch(kijk.state.tr.setSelection(keuze));
    const dom = kijk.nodeDOM(blok.pos) as HTMLElement | null;
    gebeurtenis.dataTransfer.effectAllowed = "move";
    gebeurtenis.dataTransfer.setData("text/plain", eigen.textContent);
    if (dom) gebeurtenis.dataTransfer.setDragImage(dom, 12, 12);
    // `node` erbij, en niet alleen `slice`: bij het neerzetten haalt ProseMirror het blok dan
    // in zijn geheel weg op de oude plek. Zonder dat veld valt hij terug op "verwijder de
    // selectie", en dat laat een lege regel achter waar het blok stond.
    (kijk as any).dragging = { slice: keuze.content(), move: true, node: keuze };
  }

  function dupliceer() {
    const eigen = knoop();
    if (!eigen) return;
    editor.chain().focus().insertContentAt(blok.pos + eigen.nodeSize, eigen.toJSON()).run();
    setMenu(false);
  }

  function verwijder() {
    const eigen = knoop();
    if (!eigen) return;
    editor.chain().focus().deleteRange({ from: blok.pos, to: blok.pos + eigen.nodeSize }).run();
    setMenu(false);
  }

  async function kopieer() {
    const eigen = knoop();
    if (!eigen) return;
    try {
      // Als markdown en niet als platte tekst: plak je het in een ander programma, dan blijft
      // een lijstje een lijstje.
      await navigator.clipboard.writeText(naarMarkdown({ type: "doc", content: [eigen.toJSON() as never] }));
      opMelding?.("Blok gekopieerd als markdown");
    } catch {
      opMelding?.("Kopiëren lukte niet — je browser geeft geen toegang tot het klembord.");
    }
    setMenu(false);
  }

  return (
    <div className="blokgreepvak" style={{ top: blok.top, left: blok.links, height: blok.hoogte }} ref={vakRef}>
      <button
        className="blokgreep"
        draggable
        title="Slepen om te verplaatsen, klikken voor het menu"
        aria-label="Blokmenu"
        aria-haspopup="menu"
        aria-expanded={menu}
        onDragStart={opSleepStart}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!menu) kiesBlok();
          setMenu((open) => !open);
        }}
      >
        <Icoon naam="greep" grootte={16} />
      </button>

      {menu && (
        <div className="blokmenu" role="menu">
          <div className="blokmenukop">Omzetten naar</div>
          {OMZETTINGEN.map((om) => (
            <button
              key={om.naam}
              role="menuitem"
              onClick={() => {
                if (kiesBlok()) om.doe(editor);
                setMenu(false);
              }}
            >
              <span className="blokmerk">{om.merk}</span> {om.naam}
            </button>
          ))}
          <div className="blokmenukop">Dit blok</div>
          <button role="menuitem" onClick={dupliceer}>
            <Icoon naam="kopie" grootte={15} /> Dupliceren
          </button>
          <button role="menuitem" onClick={() => void kopieer()}>
            <Icoon naam="download" grootte={15} /> Kopiëren als markdown
          </button>
          <button role="menuitem" onClick={verwijder}>
            <Icoon naam="prullenbak" grootte={15} /> Verwijderen
          </button>
        </div>
      )}
    </div>
  );
}
